// PRD 05 §3.8 — copy modal.
//
// Layout (860 × ≤85vh):
//   header  "다른 문항 복사" + close icon
//   body    left (~35%) tree picker + right (~65%) search bar + results list
//   footer  [취소] [복사]
//
// Tree picker: clicking an item filters the result list to that item.
// Search bar: matches QID exactly or text body (case-insensitive).
// Single-select; `[복사]` enables only after a row is highlighted.
//
// On confirm the server-side copy mutation creates the new card; the page
// then opens the edit panel on that new qid so the author can fill in the
// meta fields per §3.8.4.

import { createPortal } from 'react-dom';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';

import { toast } from '@/components/feedback/useToast';
import { useDeployDirtyStore } from '@/features/pro-deployment/store/deploy-dirty.store';

import { useAllQuestionsQuery } from '../hooks/use-item-questions';
import { useDiseaseFilterOptionsQuery } from '../hooks/use-disease-filter-options';
import { useCopyQuestionMutation } from '../hooks/use-question-mutations';
import { useProTreeQuery } from '../hooks/use-questionnaire-tree';
import { useQuestionnaireDesignPageStore } from '../store/questionnaire-design-page.store';
import type { DiseaseFilterValue } from '../store/questionnaire-design-page.store';
import { buildDiseaseLookup, itemMatchesDiseaseFilter } from '../utils/disease-filter';
import { findItemNode } from '../utils/pro-tree';
import type { Question } from '../types/questionnaire-design.types';

const ANSWER_TYPE_SHORT_KEY: Record<Question['answerType'], string> = {
  SHORT_ANSWER: 'enums.answerTypeShort.shortAnswer',
  MULTI_CHOICE: 'enums.answerTypeShort.multiChoice',
  YES_NO: 'enums.answerTypeShort.yesNo',
  OX: 'enums.answerTypeShort.ox',
  TEXT: 'enums.answerTypeShort.text',
  SLIDER: 'enums.answerTypeShort.slider',
  PHOTO: 'enums.answerTypeShort.photo',
  BATTERY: 'enums.answerTypeShort.battery',
};

export function CopyQuestionModal() {
  const { t } = useTranslation('questionnaire-design');
  const copyModal = useQuestionnaireDesignPageStore((s) => s.copyModal);
  const closeCopyModal = useQuestionnaireDesignPageStore((s) => s.closeCopyModal);
  const selectedItemId = useQuestionnaireDesignPageStore((s) => s.selectedItemId);
  const openEditPanel = useQuestionnaireDesignPageStore((s) => s.openEditPanel);
  const addSurveyDirty = useDeployDirtyStore((s) => s.addSurvey);

  const { data: tree } = useProTreeQuery();
  const { data: allQuestions } = useAllQuestionsQuery();
  const { data: diseaseOptions } = useDiseaseFilterOptionsQuery();
  const copyMutation = useCopyQuestionMutation();

  const [treeItemId, setTreeItemId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [diseaseFilter, setDiseaseFilter] = useState<DiseaseFilterValue>('');
  const [selectedQid, setSelectedQid] = useState<string | null>(null);

  // Reset local state every time the modal opens so a previous run doesn't
  // leak into the new one.
  useEffect(() => {
    if (copyModal.open) {
      setTreeItemId(null);
      setSearchTerm('');
      setDiseaseFilter('');
      setSelectedQid(null);
    }
  }, [copyModal.open]);

  // SHCT-245 MF-02 — "Applicable Disease" search dimension. Same value
  // semantics as the main tree filter (''=all, 'COMMON', 'GRP:<name>', ICD code).
  const diseaseLookup = useMemo(
    () =>
      buildDiseaseLookup(
        (diseaseOptions ?? []).map((o) => o.group),
        new Map((diseaseOptions ?? []).map((o) => [o.group.id, o.codes])),
      ),
    [diseaseOptions],
  );

  // Escape closes; same dirty-free convention as other admin-web modals.
  useEffect(() => {
    if (!copyModal.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCopyModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [copyModal.open, closeCopyModal]);

  // Index — for each item, its display path (Area code · SubArea code · Item
  // label) so the result row can show "1-2-1 · 복약 상태" without re-walking
  // the tree per render.
  const itemPathById = useMemo(() => {
    const map = new Map<string, string>();
    for (const area of tree ?? []) {
      for (const sub of area.children ?? []) {
        for (const item of sub.children ?? []) {
          map.set(
            item.id,
            t('copyModal.itemPath', {
              code: item.code,
              label: item.label,
            }),
          );
        }
      }
    }
    return map;
  }, [tree, t]);

  // SHCT-862 — PRD 05 v1.34 §3.8.3: the candidate pool follows the entry point.
  //
  // 컬럼 헤더 → top-level questions only. A sub-question copied from here would be appended at
  // top level and become a main question, which is exactly the 「하위문항 자동 변환 차단」 §3.8.3
  // forbids.
  // 메인 문항 카드 → sub-questions are included (재귀 평탄화), because reusing one from another
  // item is the whole reason this entry point exists. The copy lands under `parentQid`, so it
  // stays a sub-question and nothing is converted.
  const isSubEntry = copyModal.entryPoint === 'SUB';
  const sourcePool = useMemo(
    () =>
      isSubEntry
        ? (allQuestions ?? [])
        : (allQuestions ?? []).filter((q) => q.parentQid === null),
    [allQuestions, isSubEntry],
  );

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    // [ADM.046] — a candidate is in or out of the disease filter by the Scope of the item it belongs
    // to, not by anything stored on the question. Resolved per item and cached, because the pool can
    // hold every question in the tree and each one would otherwise re-walk it.
    const itemIncluded = new Map<string, boolean>();
    const includes = (itemId: string) => {
      if (!diseaseFilter) return true;
      const cached = itemIncluded.get(itemId);
      if (cached !== undefined) return cached;
      const node = tree ? findItemNode(tree, itemId) : null;
      // An item the tree does not know is left in rather than silently dropped from the candidates.
      const ok = node ? itemMatchesDiseaseFilter(node, diseaseFilter, diseaseLookup) : true;
      itemIncluded.set(itemId, ok);
      return ok;
    };
    return sourcePool.filter((q) => {
      if (treeItemId && q.itemId !== treeItemId) return false;
      if (!includes(q.itemId)) return false;
      if (!term) return true;
      return (
        q.qid.toLowerCase().includes(term) ||
        q.text.toLowerCase().includes(term)
      );
    });
  }, [sourcePool, searchTerm, treeItemId, diseaseFilter, diseaseLookup, tree]);

  const handleConfirm = async () => {
    if (
      !selectedQid ||
      !selectedItemId ||
      !copyModal.targetColumn ||
      // A SUB copy with no parent has nowhere to land; refuse rather than silently
      // creating a main question (SHCT-862).
      (isSubEntry && !copyModal.parentQid)
    ) {
      return;
    }
    try {
      const created = await copyMutation.mutateAsync({
        qid: selectedQid,
        input: {
          targetItemId: selectedItemId,
          targetColumn: copyModal.targetColumn,
          ...(isSubEntry && copyModal.parentQid
            ? { targetParentQid: copyModal.parentQid }
            : {}),
        },
      });
      addSurveyDirty(created.qid);
      closeCopyModal();
      toast.info(t('copyModal.successToast'));
      // §3.8.5 step 2 — auto-open the edit panel so the author can complete
      // (or override) the meta that the server defaulted to COMMON.
      openEditPanel(created.qid);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('copyModal.errorToast');
      toast.error(msg);
    }
  };

  if (!copyModal.open) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.45)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
      onClick={closeCopyModal}
      role="dialog"
      aria-modal="true"
      aria-labelledby="copy-survey-title"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: '0 20px 60px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '85vh',
          width: 860,
          maxWidth: 'calc(100vw - 48px)',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-4) var(--space-6)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <h2
            id="copy-survey-title"
            style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, margin: 0 }}
          >
            {/* §3.8.2 — the modal title names the entry point so the operator knows where
                the copy will land before they pick a source. */}
            {t(isSubEntry ? 'copyModal.titleSub' : 'copyModal.title')}
          </h2>
          <button
            type="button"
            onClick={closeCopyModal}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              borderRadius: 'var(--radius-sm)',
              color: 'var(--muted-foreground)',
            }}
            aria-label={t('copyModal.close')}
          >
            <X size={20} />
          </button>
        </header>

        <div
          style={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            gap: 0,
            minHeight: 400,
          }}
        >
          {/* Left — tree picker */}
          <aside
            style={{
              flex: '0 0 35%',
              minWidth: 240,
              borderRight: '1px solid var(--border)',
              padding: 12,
              overflowY: 'auto',
            }}
            aria-label={t('copyModal.treeAria')}
          >
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--muted-foreground)',
                marginBottom: 8,
              }}
            >
              {t('copyModal.treeHeader')}
            </div>
            {(tree ?? []).length === 0 ? (
              <div style={{ padding: 12, color: 'var(--muted-foreground)', fontSize: 'var(--font-size-sm)' }}>
                {t('copyModal.treeEmpty')}
              </div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {(tree ?? []).map((area) => (
                  <li key={area.id} style={{ marginBottom: 8, fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                    {area.code}. {area.label}
                    <ul style={{ listStyle: 'none', paddingLeft: 12, marginTop: 4 }}>
                      {(area.children ?? []).flatMap((sub) =>
                        (sub.children ?? []).map((item) => {
                          const count = sourcePool.filter((q) => q.itemId === item.id).length;
                          const isSelected = treeItemId === item.id;
                          return (
                            <li
                              key={item.id}
                              onClick={() => setTreeItemId(item.id)}
                              style={{
                                padding: '4px 8px',
                                cursor: 'pointer',
                                borderRadius: 4,
                                fontWeight: 'normal',
                                fontSize: 'var(--font-size-sm)',
                                background: isSelected ? 'var(--badge-primary-bg)' : 'transparent',
                                color: isSelected ? 'var(--badge-primary-text)' : undefined,
                              }}
                            >
                              {item.code}. {item.label} ({count})
                            </li>
                          );
                        }),
                      )}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          {/* Right — search + results */}
          <section
            style={{
              flex: 1,
              minWidth: 0,
              padding: 12,
              display: 'flex',
              flexDirection: 'column',
            }}
            aria-label={t('copyModal.searchAria')}
          >
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: 8,
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  flex: 1,
                }}
              >
                <Search size={14} color="var(--muted-foreground)" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('copyModal.searchPlaceholder')}
                  style={{
                    flex: 1,
                    border: 0,
                    outline: 0,
                    fontSize: 'var(--font-size-sm)',
                    background: 'transparent',
                  }}
                />
              </div>
              <select
                className="form-field__select"
                value={diseaseFilter}
                onChange={(e) => setDiseaseFilter(e.target.value as DiseaseFilterValue)}
                aria-label={t('copyModal.diseaseFilterAria')}
                style={{ flex: '0 0 200px', fontSize: 'var(--font-size-sm)' }}
              >
                <option value="">{t('filter.all')}</option>
                <option value="COMMON">{t('filter.commonOnly')}</option>
                {(diseaseOptions ?? []).map(({ group, codes }) => (
                  <optgroup key={group.id} label={group.name}>
                    <option value={`GRP:${group.name}`}>
                      {t('filter.groupAll', { name: group.name })}
                    </option>
                    {codes
                      .filter((c) => c.status === 'ACTIVE')
                      .map((c) => (
                        <option key={c.id} value={c.code}>
                          {c.code} {c.name}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              {filtered.length === 0 ? (
                <div
                  style={{
                    padding: 24,
                    textAlign: 'center',
                    color: 'var(--muted-foreground)',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  {treeItemId || searchTerm || diseaseFilter
                    ? t('copyModal.noResults')
                    : t('copyModal.emptyHint')}
                </div>
              ) : (
                filtered.map((q) => {
                  const isSelected = q.qid === selectedQid;
                  const preview = q.text.length > 80 ? `${q.text.slice(0, 80)}…` : q.text;
                  return (
                    <div
                      key={q.qid}
                      onClick={() => setSelectedQid(q.qid)}
                      style={{
                        display: 'flex',
                        gap: 8,
                        padding: '8px 12px',
                        borderBottom: '1px solid var(--border)',
                        cursor: 'pointer',
                        alignItems: 'center',
                        background: isSelected ? 'var(--badge-primary-bg)' : 'transparent',
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          minWidth: 70,
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--info)',
                          // The prefix makes this cell wider than a bare QID; without this the
                          // longer label wraps and the row grows a second line.
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {q.qid}
                      </span>
                      <span
                        style={{
                          flex: 1,
                          fontSize: 'var(--font-size-sm)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {preview}
                      </span>
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          background: 'var(--secondary)',
                          padding: '2px 6px',
                          borderRadius: 4,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t(ANSWER_TYPE_SHORT_KEY[q.answerType])}
                      </span>
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--muted-foreground)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {/* SHCT-862 — the SUB entry point pools main questions AND sub-questions,
                            and a QID alone does not say which level a row sits at. The marker goes
                            on the 항목 경로 cell, which is where the prototype
                            (`survey-editor.html` renderCopyResults) puts it — the first attempt put
                            it on the QID and QC re-tested against the prototype. Only sub-questions
                            carry it, so the column entry point (main questions only) is unchanged. */}
                        {q.parentQid !== null ? `${t('copyModal.subPrefix')} ` : ''}
                        {itemPathById.get(q.itemId) ?? q.itemId}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </section>
        </div>

        <footer
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 8,
            padding: 'var(--space-4) var(--space-6)',
            borderTop: '1px solid var(--border)',
          }}
        >
          <button
            type="button"
            className="btn btn-default btn-ghost"
            onClick={closeCopyModal}
            disabled={copyMutation.isPending}
          >
            {t('copyModal.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-default btn-primary"
            onClick={handleConfirm}
            disabled={!selectedQid || copyMutation.isPending}
          >
            {copyMutation.isPending
              ? t('copyModal.confirmLoading')
              : t('copyModal.confirm')}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
